// src/components/VendorDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

const VendorRFQDetail = () => {
    const { vendorId } = useParams();
    const [vendor, setVendor] = useState(null);
    const [rfqs, setRfqs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            // Fetch both vendor details and RFQ history in parallel
            const [vendorResponse, rfqsResponse] = await Promise.all([
                api.get(`/vendors/${vendorId}/`),
                api.get(`/vendors/${vendorId}/rfqs/`)
            ]);
            setVendor(vendorResponse.data);
            setRfqs(rfqsResponse.data);
        } catch (err) {
            console.error("Error fetching vendor details:", err);
            setError("Failed to load vendor data. Please check the ID and try again.");
        } finally {
            setIsLoading(false);
        }
    }, [vendorId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getStatusChip = (status) => {
        const styles = {
            // Invitation Status
            PENDING: 'bg-yellow-100 text-yellow-800',
            VIEWED: 'bg-blue-100 text-blue-800',
            SUBMITTED: 'bg-purple-100 text-purple-800',
            // RFQ Status
            DRAFT: 'bg-gray-200 text-gray-800',
            OPEN: 'bg-cyan-100 text-cyan-800',
            AWARDED: 'bg-green-100 text-green-800',
            CLOSED: 'bg-red-100 text-red-800',
            CANCELLED: 'bg-gray-400 text-white',
        };
        return (
            <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${styles[status] || 'bg-gray-100'}`}>
                {status.replace('_', ' ')}
            </span>
        );
    };

    if (isLoading) {
        return <div className="text-center p-10">Loading vendor details...</div>;
    }

    if (error) {
        return <div className="text-center p-10 bg-red-50 text-red-700">{error}</div>;
    }

    if (!vendor) {
        return <div className="text-center p-10">No vendor found.</div>;
    }

    return (
        <div className="min-h-screen bg-gray-100 p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header Section */}
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-4xl font-bold text-gray-900">{vendor.name}</h1>
                            <p className="mt-2 text-lg text-gray-600">Country: {vendor.country}</p>
                            <p className="text-sm text-gray-500">Expertise: {vendor.expertise}</p>
                        </div>
                        <Link
                            to="/vendors"
                            className="text-indigo-600 hover:text-indigo-800"
                        >
                            ← Back to Dashboard
                        </Link>
                    </div>
                    {/* You can add more vendor details here */}
                </div>

                {/* RFQ History Section */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">RFQ History</h2>
                    {rfqs.length === 0 ? (
                        <p className="text-gray-500 italic">This vendor has not been invited to any RFQs yet.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFQ Name</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">RFQ Status</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Invitation Status</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Submitted Cost</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invited On</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Result</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {rfqs.map(invitation => (
                                        <tr key={invitation.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm font-medium text-gray-900">
                                                <Link to={`/rfqs/${invitation.rfq_id}`} className="text-indigo-600 hover:underline">
                                                    {invitation.rfq_name}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-4 text-sm text-center">{getStatusChip(invitation.rfq_status)}</td>
                                            <td className="px-4 py-4 text-sm text-center">{getStatusChip(invitation.status)}</td>
                                            <td className="px-4 py-4 text-sm text-gray-600 text-right">
                                                {invitation.overall_cost ? `$${parseFloat(invitation.overall_cost).toFixed(2)}` : 'N/A'}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {new Date(invitation.created_at).toLocaleDateString()}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-center font-semibold">
                                                {invitation.is_awarded_to_this_vendor ? (
                                                    <span className="text-green-600">🏆 Awarded</span>
                                                ) : invitation.rfq_status === 'AWARDED' && invitation.status === 'SUBMITTED' ? (
                                                    <span className="text-red-600">Not Awarded</span>
                                                ) : (
                                                    <span className="text-gray-500">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VendorRFQDetail;